
import { useState, useMemo } from 'react';
import { TeacherProfile } from './useTeacherProfiles';

export interface FilterState {
  subject: string;
  gradeLevel: string;
  courseType: string;
  specialized: string;
}

export const useTeacherProfilesFilters = (profiles: TeacherProfile[] = []) => {
  const [filters, setFilters] = useState<FilterState>({
    subject: 'all',
    gradeLevel: 'all',
    courseType: 'all',
    specialized: 'all'
  });

  // Extract unique values for filter options
  const filterOptions = useMemo(() => {
    const subjects = new Set<string>();
    const gradeLevels = new Set<string>();
    const courseTypes = new Set<string>();
    const specialized = new Set<string>();

    profiles.forEach(profile => {
      if (Array.isArray(profile.subjects)) {
        profile.subjects.forEach((subjectData: any) => {
          if (subjectData && typeof subjectData === 'object') {
            if (subjectData.subject) subjects.add(subjectData.subject);
            if (subjectData.grade_level) gradeLevels.add(subjectData.grade_level);
            if (subjectData.course_type) courseTypes.add(subjectData.course_type);
          }
        });
      }

      if (Array.isArray(profile.specialized)) {
        profile.specialized.forEach((spec: any) => {
          if (spec && spec.name) specialized.add(spec.name);
        });
      }
    });

    return {
      subjects: Array.from(subjects).sort(),
      gradeLevels: Array.from(gradeLevels).sort(),
      courseTypes: Array.from(courseTypes).sort(),
      specialized: Array.from(specialized).sort()
    };
  }, [profiles]);

  // Filter profiles based on current filter state
  const filteredProfiles = useMemo(() => {
    return profiles.filter(profile => {
      if (!Array.isArray(profile.subjects)) return false;

      const matchesSubjects = profile.subjects.some((subjectData: any) => {
        if (!subjectData || typeof subjectData !== 'object') return false;

        const matchesSubject = filters.subject === 'all' || subjectData.subject === filters.subject;
        const matchesGradeLevel = filters.gradeLevel === 'all' || subjectData.grade_level === filters.gradeLevel;
        const matchesCourseType = filters.courseType === 'all' || subjectData.course_type === filters.courseType;

        return matchesSubject && matchesGradeLevel && matchesCourseType;
      });

      const matchesSpecialized = filters.specialized === 'all' || 
        (Array.isArray(profile.specialized) && 
         profile.specialized.some((spec: any) => spec && spec.name === filters.specialized));

      return matchesSubjects && matchesSpecialized;
    });
  }, [profiles, filters]);

  const updateFilter = (filterType: keyof FilterState, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      subject: 'all',
      gradeLevel: 'all',
      courseType: 'all',
      specialized: 'all'
    });
  };

  const hasActiveFilters = filters.subject !== 'all' || 
                          filters.gradeLevel !== 'all' || 
                          filters.courseType !== 'all' || 
                          filters.specialized !== 'all';

  return {
    filters,
    filteredProfiles,
    filterOptions,
    updateFilter,
    clearFilters,
    hasActiveFilters
  };
};
